'use client';
import {Box, Link} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';
import {trackFooterContactMethodClick} from '../../analytics';
import {externalLinkProps, Typography} from '../primitives';
import type {ContactRowView} from './contact-href';
import {ColumnHeading, ColumnLinks} from './styles';

interface FooterContactColumnProps {
  headingKey?: string;
  rows: ContactRowView[];
}

const ContactRow = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
}));

const RowIcon = styled('span')(({theme}) => ({
  fontSize: 22,
  color: theme.palette.colors.icon.primary.default,
}));

const ContactRowLink = styled(Link)({
  color: 'inherit',
  textDecoration: 'none',
  '&:hover': {textDecoration: 'underline'},
});

function ContactValue({row}: {row: ContactRowView}) {
  const {t} = useTranslation();
  const text = row.value ?? t(row.labelKey);
  const dir = row.value && row.method !== 'address' ? 'ltr' : undefined;
  if (!row.href) {
    return (
      <Box component="span" dir={dir}>
        {text}
      </Box>
    );
  }
  return (
    <ContactRowLink
      href={row.href}
      underline="none"
      dir={dir}
      onClick={() => {
        trackFooterContactMethodClick(row.method);
      }}
      {...externalLinkProps(row.href)}
    >
      {text}
    </ContactRowLink>
  );
}

function ContactText({row}: {row: ContactRowView}) {
  return (
    <Typography
      size="body-2"
      weight="regular"
      lineHeight="22"
      as="span"
      // Contact values are unbreakable tokens (an email is one long "word"), so
      // their min-content width propagates up and widens the whole document on
      // narrow screens. `anywhere` — not `break-word` — also shrinks that
      // min-content contribution, which is what the overflow follows from.
      sx={{color: theme => theme.palette.colors.text.neutral.inverseMuted, overflowWrap: 'anywhere'}}
    >
      <ContactValue row={row} />
    </Typography>
  );
}

function ContactDetailText({row}: {row: ContactRowView}) {
  const {t} = useTranslation();
  return (
    <Typography size="body-2" weight="regular" lineHeight="22" as="span" sx={{overflowWrap: 'anywhere'}}>
      <Box component="span" sx={{color: theme => theme.palette.colors.text.neutral.subtle}}>{`${t(row.labelKey)} `}</Box>
      <Box component="span" sx={{color: theme => theme.palette.colors.text.primary.inverse}}>
        <ContactValue row={row} />
      </Box>
    </Typography>
  );
}

export function FooterContactColumn({headingKey, rows}: FooterContactColumnProps) {
  const {t} = useTranslation();
  return (
    <Box>
      {headingKey && <ColumnHeading>{t(headingKey)}</ColumnHeading>}
      <ColumnLinks component="ul">
        {rows.map(row => (
          <Box component="li" key={row.labelKey}>
            <ContactRow>
              <RowIcon>{row.icon}</RowIcon>
              {row.inlineLabel ? <ContactDetailText row={row} /> : <ContactText row={row} />}
            </ContactRow>
          </Box>
        ))}
      </ColumnLinks>
    </Box>
  );
}

FooterContactColumn.displayName = 'FooterContactColumn';
